import React from 'react'
import styled from 'styled-components'
import { DSText, DSSpacer } from '@zoe/ds-web'

const GraphCard = styled.div`
  background: #ffffff;
  border-radius: 16px;
  padding: 20px 16px 16px;
`

const SVG = styled.svg`
  display: block;
  width: 100%;
  height: auto;
`

export type SymptomReductionGraphProps = {
  startScore: number
  endScore: number
  durationMs?: number
}

// Format today / today + 6 weeks as e.g. "24 June".
const formatDate = (d: Date) => `${d.getDate()} ${d.toLocaleString('en-GB', { month: 'long' })}`

// Round up to nearest 10 for the y-axis max, down to nearest 10 for the min,
// then place 2 intermediate reference lines (4 lines total).
const axisBounds = (startScore: number, endScore: number) => {
  let max = Math.ceil(startScore / 10) * 10
  let min = Math.floor(endScore / 10) * 10
  if (max <= min) max = min + 30 // guarantee a visible range
  min = Math.max(0, min)
  const step = (max - min) / 3
  const labels = [max, max - step, max - 2 * step, min]
  return { max, min, labels }
}

export const SymptomReductionGraph: React.FC<SymptomReductionGraphProps> = ({
  startScore,
  endScore,
  durationMs = 2400,
}) => {
  // Memoised so the dates are stable across renders.
  const { today, end } = React.useMemo(() => {
    const t = new Date()
    const e = new Date(t)
    e.setDate(e.getDate() + 42) // 6 weeks
    return { today: t, end: e }
  }, [])

  const { max, min, labels } = axisBounds(startScore, endScore)

  const width = 311
  const graphTop = 14
  const graphBottom = 132
  const plotH = graphBottom - graphTop
  const padLeft = 30
  const padRight = 26
  const xStart = padLeft
  const xEnd = width - padRight
  const xLabelsY = graphBottom + 28 // padding above the x-axis date labels
  const height = xLabelsY + 6

  const yFor = (v: number) => graphTop + ((max - v) / (max - min)) * plotH
  const startY = yFor(startScore)
  const endY = yFor(endScore)

  // Jagged (straight-segment) descending line — matches the Figma design's
  // angular look rather than a smooth curve. Each [t, descentFraction] pair is a
  // vertex; descent is slightly irregular to give the hand-drawn jaggedness.
  const span = xEnd - xStart
  const dropY = endY - startY
  const jaggedStops: [number, number][] = [
    [0, 0],
    [0.1, 0.04],
    [0.24, 0.18],
    [0.38, 0.31],
    [0.5, 0.45],
    [0.62, 0.55],
    [0.76, 0.78],
    [0.88, 0.9],
    [1, 1],
  ]
  const d = jaggedStops
    .map(([t, f], i) => `${i === 0 ? 'M' : 'L'} ${xStart + span * t} ${startY + dropY * f}`)
    .join(' ')

  // Yellow projected-score bubble, centred directly above the coral end dot.
  const bubbleW = 48
  const bubbleH = 28
  const bubbleCx = xEnd
  const bubbleCy = endY - 24
  const revealBegin = `${durationMs}ms`

  return (
    <GraphCard>
      <DSText variant="fluid-paragraph-200" weight="semiBold" align="center">
        Symptom Severity Score
      </DSText>
      <DSSpacer size={12} direction="vertical" />
      <SVG viewBox={`0 0 ${width} ${height}`}>
        {/* Gridlines + y-axis labels (Label 100/Regular) */}
        {labels.map((v) => {
          const y = yFor(v)
          return (
            <g key={v}>
              <line x1={xStart} x2={xEnd} y1={y} y2={y} stroke="#EDE8DE" strokeWidth={1} />
              <text
                x={xStart - 8}
                y={y + 5}
                textAnchor="end"
                fontFamily="'LotaGrotesqueAlt1', sans-serif"
                fontSize="14"
                fill="#6F7273"
              >
                {Math.round(v)}
              </text>
            </g>
          )
        })}

        {/* Descending line */}
        <path d={d} fill="none" stroke="#2D3246" strokeWidth={3} strokeLinecap="round" strokeLinejoin="round" />

        {/* Coral dot travels down the line and freezes at the bottom. key={d}
            remounts it so the motion replays if the path changes. */}
        <circle key={d} r={6} fill="#F2785C">
          <animateMotion dur={revealBegin} fill="freeze" path={d} />
        </circle>

        {/* Projected score bubble — revealed once the dot reaches the end */}
        <g opacity={0}>
          <animate attributeName="opacity" from="0" to="1" begin={revealBegin} dur="250ms" fill="freeze" />
          <rect
            x={bubbleCx - bubbleW / 2}
            y={bubbleCy - bubbleH / 2}
            width={bubbleW}
            height={bubbleH}
            rx={14}
            fill="#FFD100"
          />
          {/* Downward pointer from the bubble's bottom-centre toward the dot */}
          <polygon
            points={`${bubbleCx - 6},${bubbleCy + bubbleH / 2 - 1} ${bubbleCx + 6},${
              bubbleCy + bubbleH / 2 - 1
            } ${bubbleCx},${bubbleCy + bubbleH / 2 + 6}`}
            fill="#FFD100"
          />
          <text
            x={bubbleCx}
            y={bubbleCy + 5}
            textAnchor="middle"
            fontFamily="'LotaGrotesqueAlt1', sans-serif"
            fontSize="15"
            fontWeight="600"
            fill="#2D3246"
          >
            {endScore}
          </text>
        </g>

        {/* X-axis date labels, aligned to the start/end of the gridlines */}
        <text
          x={xStart}
          y={xLabelsY}
          textAnchor="start"
          fontFamily="'LotaGrotesqueAlt1', sans-serif"
          fontSize="14"
          fill="#6F7273"
        >
          {formatDate(today)}
        </text>
        <text
          x={xEnd}
          y={xLabelsY}
          textAnchor="end"
          fontFamily="'LotaGrotesqueAlt1', sans-serif"
          fontSize="14"
          fill="#6F7273"
        >
          {formatDate(end)}
        </text>
      </SVG>
    </GraphCard>
  )
}
